<template>
  <div class="space-y-3">
    <!-- Search -->
    <input
      v-model="searchQuery"
      type="text"
      placeholder="Search ideas..."
      class="w-full text-sm rounded border border-gray-200 px-3 py-1.5 text-gray-800 placeholder-gray-400
             focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
    />

    <div v-if="loading" class="text-sm text-gray-500 italic">Loading ideas...</div>

    <div v-else-if="!filteredIdeas.length" class="text-sm text-gray-500 italic">
      {{ searchQuery ? 'No matching ideas.' : 'No fresh ideas. Add some from the Ideas page.' }}
    </div>

    <div
      v-for="idea in filteredIdeas"
      :key="idea.id"
      class="group rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:border-gray-300 hover:bg-gray-50"
    >
      <div class="text-gray-900 font-medium leading-tight">{{ idea.display_label }}</div>
      <div class="text-xs text-gray-400 mt-0.5">{{ timeAgo(idea.created_at) }}</div>

      <!-- Action buttons -->
      <div class="flex items-center gap-2 mt-2">
        <button
          @click="addAsKnowledge(idea)"
          :disabled="!store.currentProject.value || busyId === idea.id"
          class="text-xs font-medium px-2 py-1 rounded bg-teal-50 text-teal-700 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >+ Knowledge</button>

        <button
          @click="addAsEntry(idea)"
          :disabled="!currentPublication || busyId === idea.id"
          class="text-xs font-medium px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
          :title="currentPublication ? '' : 'Select a publication first'"
        >+ Entry</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useApi } from '../../composables/useApi'

const store = inject('workbench')
const api = useApi()

const ideas = ref([])
const loading = ref(false)
const searchQuery = ref('')
const busyId = ref(null)

const currentPublication = computed(() => store.currentPublication?.value || null)

const filteredIdeas = computed(() => {
  let result = ideas.value.filter(i => i.status === 'fresh')
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(i => i.body.toLowerCase().includes(q))
  }
  return result
})

onMounted(loadIdeas)

async function loadIdeas() {
  loading.value = true
  try {
    ideas.value = await api.get('/api/ideas')
  } finally {
    loading.value = false
  }
}

async function addAsKnowledge(idea) {
  if (!store.currentProject.value) return
  busyId.value = idea.id
  try {
    const result = await api.post(`/api/ideas/${idea.id}/use_as_knowledge_item`, {
      project_id: store.currentProject.value.id,
    })
    // Update local idea status
    const idx = ideas.value.findIndex(i => i.id === idea.id)
    if (idx !== -1) ideas.value[idx] = result.idea
    // Refresh workbench project data
    if (store.currentProject.value) {
      await store.selectProject(store.currentProject.value.id)
    }
  } finally {
    busyId.value = null
  }
}

async function addAsEntry(idea) {
  if (!currentPublication.value) return
  busyId.value = idea.id
  try {
    const result = await api.post(`/api/ideas/${idea.id}/use_as_publication_entry`, {
      publication_id: currentPublication.value.id,
    })
    // Update local idea status
    const idx = ideas.value.findIndex(i => i.id === idea.id)
    if (idx !== -1) ideas.value[idx] = result.idea
    // Refresh publications
    if (store.loadPublications) {
      await store.loadPublications(store.currentProject.value.id)
    }
  } finally {
    busyId.value = null
  }
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
</script>
